import Vue from 'vue';
import Pusher from 'pusher-js';

import { recordOrderPresenceRequest, releaseOrderPresenceRequest } from './api';

export const PRESENCE_STATES = {
  VIEWING: 'viewing',
  PAYING: 'paying',
};

// Pusher requires every presence channel name to start with `presence-`.
const CHANNEL_PREFIX = 'presence-order-';

const AUTH_ENDPOINT =
  process.env.VUE_APP_PUSHER_AUTH_ENDPOINT ||
  `${process.env.VUE_APP_API_URL}pusher/auth`;

const DEFAULT_HEARTBEAT_SECONDS = 25;

// Pusher client and channel instances are deliberately kept out of the store
// state - they are not serializable and must not be made reactive by Vue.
let client = null;
const channels = {};
const heartbeats = {};

// The screen that currently owns the presence for an order, and what it says
// the guest is doing.
const active = {};

// Vue destroys the outgoing screen before it mounts the incoming one, so on
// every hop between the order, item select and payment screens the leave lands
// first. Releasing straight away would drop the guest off the order for a moment
// and race the record the next screen is about to send, so the teardown waits
// long enough for that screen to take over and call it off.
const pendingReleases = {};
const RELEASE_GRACE_MS = 3000;

let visibilityListenerAttached = false;

const channelName = uniqueOrderNumber =>
  `${CHANNEL_PREFIX}${uniqueOrderNumber}`;

const createClient = ({ rootState, rootGetters }) => {
  Pusher.logToConsole = process.env.VUE_APP_ENVIRONMENT !== 'production';

  return new Pusher(process.env.VUE_APP_PUSHER_APP_KEY, {
    cluster: 'eu',
    channelAuthorization: {
      transport: 'ajax',
      endpoint: AUTH_ENDPOINT,
      // Custom handler so that the session and the anonymous user token are
      // read at authorization time instead of when the client was created.
      customHandler: async ({ socketId, channelName: channel }, callback) => {
        try {
          const { data } = await Vue.axios.post(AUTH_ENDPOINT, {
            socket_id: socketId,
            channel_name: channel,
            session: rootState.session,
            anonymous_user_token: rootGetters.anonymousUserToken,
          });

          callback(null, data);
        } catch (error) {
          callback(error, null);
        }
      },
    },
  });
};

const stopHeartbeat = uniqueOrderNumber => {
  if (heartbeats[uniqueOrderNumber]) {
    clearInterval(heartbeats[uniqueOrderNumber]);
    delete heartbeats[uniqueOrderNumber];
  }
};

const cancelPendingRelease = uniqueOrderNumber => {
  if (pendingReleases[uniqueOrderNumber]) {
    clearTimeout(pendingReleases[uniqueOrderNumber]);
    delete pendingReleases[uniqueOrderNumber];
  }
};

const unsubscribe = uniqueOrderNumber => {
  const name = channelName(uniqueOrderNumber);

  if (channels[name]) {
    channels[name].unbind_all();
    client.unsubscribe(name);
    delete channels[name];
  }
};

const startHeartbeat = (uniqueOrderNumber, dispatch) => {
  stopHeartbeat(uniqueOrderNumber);

  const entry = active[uniqueOrderNumber];

  if (!entry) {
    return;
  }

  heartbeats[uniqueOrderNumber] = setInterval(() => {
    dispatch('recordOrderPresence', {
      uniqueOrderNumber,
      state: entry.state,
    });
  }, (entry.heartbeatSeconds || DEFAULT_HEARTBEAT_SECONDS) * 1000);
};

/**
 * Leaving the order the instant the screen goes away, rather than waiting for
 * the presence to time out.
 *
 * A locked phone gives us no time to await anything and may freeze our timers
 * before they run, so this goes out as a beacon: fire and forget, form encoded
 * so the browser treats it as a simple cross-origin request and sends it
 * without waiting to ask permission first.
 */
const beaconRelease = ({ uniqueOrderNumber, session, anonymousUserToken }) => {
  if (!navigator.sendBeacon || !session) {
    return false;
  }

  const body = new URLSearchParams({
    unique_order_number: uniqueOrderNumber,
  });

  if (anonymousUserToken) {
    // Without this the server works the guest out from the session instead and
    // would be removing somebody else's presence.
    body.append('anonymous_user_token', anonymousUserToken);
  }

  return navigator.sendBeacon(
    `${process.env.VUE_APP_API_URL}orders/presence/release/${session}`,
    body
  );
};

/**
 * Hiding the page means the guest put their phone down or switched away, so a
 * viewer stops being one straight away instead of lingering until their presence
 * expires. Somebody paying is left exactly where they are: being sent to a bank
 * app hides the page too, and that is the one moment they most need to still
 * count as paying.
 */
const attachVisibilityListener = ({ dispatch, rootState, rootGetters }) => {
  if (visibilityListenerAttached) {
    return;
  }

  document.addEventListener('visibilitychange', () => {
    const visible = document.visibilityState === 'visible';

    Object.keys(active).forEach(uniqueOrderNumber => {
      if (visible) {
        // Timers are throttled while hidden and a long enough absence expires
        // the presence outright, so it is put back rather than waited for.
        dispatch('recordOrderPresence', {
          uniqueOrderNumber,
          state: active[uniqueOrderNumber].state,
        });
        startHeartbeat(uniqueOrderNumber, dispatch);

        return;
      }

      if (active[uniqueOrderNumber].state !== PRESENCE_STATES.VIEWING) {
        return;
      }

      stopHeartbeat(uniqueOrderNumber);
      beaconRelease({
        uniqueOrderNumber,
        session: rootState.session,
        anonymousUserToken: rootGetters.anonymousUserToken,
      });
    });
  });

  visibilityListenerAttached = true;
};

const initialState = {
  // Everyone on an order, as the server sees them - which also covers a guest
  // who is away on a payment provider's website and connected to nothing.
  presences: {},
  // Our own actor id per order. Broadcasts carry the same list for everyone, so
  // this is what tells us which entry is us.
  actorIds: {},
};

export default {
  state: initialState,
  mutations: {
    SET_ORDER_PRESENCES(state, { uniqueOrderNumber, presences }) {
      Vue.set(state.presences, uniqueOrderNumber, presences);
    },
    SET_ORDER_PRESENCE_ACTOR_ID(state, { uniqueOrderNumber, actorId }) {
      Vue.set(state.actorIds, uniqueOrderNumber, actorId);
    },
    CLEAR_ORDER_PRESENCES(state, uniqueOrderNumber) {
      Vue.delete(state.presences, uniqueOrderNumber);
      Vue.delete(state.actorIds, uniqueOrderNumber);
    },
  },
  actions: {
    async ensureAnonymousUserToken({ dispatch, rootGetters }) {
      if (rootGetters.anonymousUserToken) {
        return rootGetters.anonymousUserToken;
      }

      await dispatch('generateAnonymousUserToken');

      return rootGetters.anonymousUserToken;
    },
    /**
     * Puts the guest on an order: subscribes to its presence channel, tells the
     * server what they are doing and keeps saying so until they leave.
     */
    async joinOrderPresence(
      { commit, dispatch, rootState, rootGetters },
      { uniqueOrderNumber, state, ownerId }
    ) {
      if (!uniqueOrderNumber || !rootState.session) {
        return;
      }

      // The screen being left is on its way out; keep its presence alive.
      cancelPendingRelease(uniqueOrderNumber);

      active[uniqueOrderNumber] = { ownerId, state };
      attachVisibilityListener({ dispatch, rootState, rootGetters });

      await dispatch('ensureAnonymousUserToken');

      if (active[uniqueOrderNumber]?.ownerId !== ownerId) {
        return;
      }

      const name = channelName(uniqueOrderNumber);

      if (!channels[name]) {
        if (!client) {
          client = createClient({ rootState, rootGetters });
        }

        const channel = client.subscribe(name);
        channels[name] = channel;

        // The event carries the whole list, so nothing has to be fetched back.
        channel.bind('order-presence-updated', ({ presences }) => {
          commit('SET_ORDER_PRESENCES', {
            uniqueOrderNumber,
            presences: presences || [],
          });
        });
        channel.bind('pusher:subscription_error', error => {
          console.error(`Failed to join presence channel ${name}`, error);
          unsubscribe(uniqueOrderNumber);
        });
      }

      const recorded = await dispatch('recordOrderPresence', {
        uniqueOrderNumber,
        state,
      });

      if (active[uniqueOrderNumber]?.ownerId !== ownerId) {
        return;
      }

      active[uniqueOrderNumber].heartbeatSeconds = recorded?.heartbeat_seconds;
      startHeartbeat(uniqueOrderNumber, dispatch);
    },
    /**
     * Takes the guest off an order: stops the heartbeat, leaves the channel and,
     * unless the caller is holding a payment open, tells the server they are
     * gone. Does nothing once another screen has taken the presence over, which
     * is what happens on every route change.
     */
    leaveOrderPresence(
      { commit, dispatch },
      { uniqueOrderNumber, ownerId, keepServerPresence = false }
    ) {
      if (
        !uniqueOrderNumber ||
        active[uniqueOrderNumber]?.ownerId !== ownerId
      ) {
        return;
      }

      delete active[uniqueOrderNumber];
      stopHeartbeat(uniqueOrderNumber);
      cancelPendingRelease(uniqueOrderNumber);

      // Held off briefly, so that a screen taking over can call it off before
      // the guest is dropped from the order and put straight back on it.
      pendingReleases[uniqueOrderNumber] = setTimeout(() => {
        delete pendingReleases[uniqueOrderNumber];

        unsubscribe(uniqueOrderNumber);
        commit('CLEAR_ORDER_PRESENCES', uniqueOrderNumber);

        // A payment already under way keeps its presence on the server: the
        // guest is off at their bank, not gone.
        if (!keepServerPresence) {
          dispatch('releaseOrderPresence', { uniqueOrderNumber });
        }
      }, RELEASE_GRACE_MS);
    },
    async recordOrderPresence(
      { commit, dispatch },
      { uniqueOrderNumber, state }
    ) {
      return dispatch('runOrderPresenceRequest', {
        uniqueOrderNumber,
        state,
        request: recordOrderPresenceRequest,
      }).then(data => {
        if (data) {
          commit('SET_ORDER_PRESENCE_ACTOR_ID', {
            uniqueOrderNumber,
            actorId: data.actor_id,
          });
          commit('SET_ORDER_PRESENCES', {
            uniqueOrderNumber,
            presences: data.presences || [],
          });
        }

        return data;
      });
    },
    async releaseOrderPresence({ dispatch }, { uniqueOrderNumber }) {
      return dispatch('runOrderPresenceRequest', {
        uniqueOrderNumber,
        request: releaseOrderPresenceRequest,
      });
    },
    async runOrderPresenceRequest(
      { dispatch, rootState, rootGetters },
      { uniqueOrderNumber, state, request }
    ) {
      if (!uniqueOrderNumber || !rootState.session) {
        return null;
      }

      await dispatch('ensureAnonymousUserToken');

      try {
        return await request({
          session: rootState.session,
          uniqueOrderNumber,
          anonymousUserToken: rootGetters.anonymousUserToken,
          state,
        });
      } catch (error) {
        console.error('Order presence request failed', error);
        return null;
      }
    },
  },
  getters: {
    getOrderPresences: state => uniqueOrderNumber =>
      state.presences[uniqueOrderNumber] || [],
    getOrderPresencesInState: (_state, getters) => (uniqueOrderNumber, state) =>
      getters
        .getOrderPresences(uniqueOrderNumber)
        .filter(presence => presence.state === state),
    getOrderIsBeingPaidByOthers: (state, getters) => uniqueOrderNumber =>
      getters
        .getOrderPresencesInState(uniqueOrderNumber, PRESENCE_STATES.PAYING)
        .some(
          presence => presence.actor_id !== state.actorIds[uniqueOrderNumber]
        ),
  },
};
